import { safeGetItem, safeRemoveItem, safeSetItem } from "@/utils/safeStorage";
import { useEffect, useState } from "react";

// Survives the reload that a fresh install usually needs before the page can
// reach it, so the prompt is still there on the other side.
const STORAGE_KEY = "EXTENSION_INSTALL_STARTED";

// Chrome hands a page its messaging bridge at load time, so a just-installed
// extension needs a beat — and often that reload — before the poll sees it.
// Long enough that an install which simply worked never trips the prompt.
const GRACE_MS = 4000;

type Props = {
  /** Desktop Chrome, the only place this extension can be installed at all. */
  eligible: boolean;
  /** The extension answered the poll. */
  installed: boolean;
};

/**
 * The prompt for an extension that was installed but never answered.
 *
 * Chrome tells a web page nothing about a disabled extension: it looks exactly
 * like one that was never installed, and the extension cannot speak up for
 * itself while it is off. So this is not a detection — it is the install click
 * plus a return to the tab, which is the whole of what we get.
 */
export const useExtensionInstallPrompt = ({ eligible, installed }: Props) => {
  const [installStarted, setInstallStarted] = useState(
    () => eligible && safeGetItem(STORAGE_KEY) === "true",
  );
  const [gracePassed, setGracePassed] = useState(false);

  const markInstallStarted = () => {
    safeSetItem(STORAGE_KEY, "true");
    setInstallStarted(true);
  };

  useEffect(() => {
    // The extension answered, so the click it was remembering is spent. Without
    // this the key outlives what it describes, and someone who installed months
    // ago and has since removed the extension is prompted about an install they
    // no longer remember making.
    if (installed) {
      safeRemoveItem(STORAGE_KEY);
      return;
    }
    if (!eligible || !installStarted) return;

    let timer: ReturnType<typeof setTimeout>;
    // Only time spent on this tab counts: the grace is for the install
    // finishing, and nothing finishes while they are reading the Web Store.
    const arm = () => {
      clearTimeout(timer);
      if (document.hidden) return;
      timer = setTimeout(() => setGracePassed(true), GRACE_MS);
    };

    arm();
    document.addEventListener("visibilitychange", arm);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", arm);
    };
  }, [eligible, installStarted, installed]);

  return {
    markInstallStarted,
    showInstallPrompt: eligible && installStarted && gracePassed && !installed,
  };
};
